import env from 'src/env'
import { localStorageKey } from 'src/context/type'

export type AiChatTurn = {
  /** 'support' is a human from the Yodo1 team, delivered by polling. */
  role: 'user' | 'assistant' | 'support'
  content: string
  /** Set on support messages so the poll never appends the same one twice. */
  id?: string
  at?: number
  /** Downscaled data URLs, max 3, on user and support turns. */
  images?: { url: string; name?: string }[]
}

export type AiStreamHandlers = {
  onToken: (text: string) => void
  onTool: (name: string, status: 'running' | 'done' | 'failed') => void
  /** Discard anything streamed so far - it was pre-tool-call narration. */
  onReset: () => void
  /** Updated rolling summary of turns that fell out of the recent window. */
  onSummary: (summary: string) => void
  /** A support person has taken over; the agent will not answer. */
  onHandover: () => void
  onError: (message: string) => void
  onDone: () => void
}

/**
 * Streams an assistant turn over SSE.
 *
 * This deliberately bypasses `portalService` - axios 0.21 buffers the whole
 * response body, so a streamed answer would only appear once it was complete.
 */
export const streamAiChat = async (
  messages: AiChatTurn[],
  handlers: AiStreamHandlers,
  signal: AbortSignal,
  /** Summary from a previous turn, so the server only folds in new overflow. */
  summary: string | null = null,
  /** Groups turns into one conversation in the admin dashboard. */
  conversationId: string | null = null
): Promise<void> => {
  const token = localStorage.getItem(localStorageKey.ACCESS_TOKEN)
  if (!token) {
    handlers.onError('Your session has expired. Please sign in again.')
    handlers.onDone()
    return
  }

  let response: Response
  try {
    response = await fetch(`${env.aiUrl}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        // The model only knows user/assistant. A human reply is still context
        // it should see if the chat is handed back, so it is sent as assistant.
        messages: messages.map(m =>
          m.role === 'support'
            ? {
                role: 'assistant' as const,
                content: `[Yodo1 support] ${m.content}`,
              }
            : { role: m.role, content: m.content }
        ),
        summary,
        conversationId,
      }),
      signal,
    })
  } catch (error) {
    if (!signal.aborted) {
      handlers.onError('Could not reach the assistant service.')
    }
    handlers.onDone()
    return
  }

  if (!response.ok || !response.body) {
    handlers.onError(
      response.status === 401
        ? 'Your session has expired. Please sign in again.'
        : `The assistant service returned an error (${response.status}).`
    )
    handlers.onDone()
    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })

      // SSE frames are separated by a blank line; keep any partial tail.
      const frames = buffer.split('\n\n')
      buffer = frames.pop() ?? ''

      for (const frame of frames) {
        dispatchFrame(frame, handlers)
      }
    }
  } catch (error) {
    if (!signal.aborted) {
      handlers.onError('The connection to the assistant was interrupted.')
    }
  } finally {
    reader.cancel().catch(() => undefined)
    handlers.onDone()
  }
}

const dispatchFrame = (frame: string, handlers: AiStreamHandlers): void => {
  let eventName = 'message'
  const dataLines: string[] = []

  for (const line of frame.split('\n')) {
    if (line.startsWith(':')) continue // heartbeat
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim()
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim())
    }
  }

  if (dataLines.length === 0) return

  let payload: any
  try {
    payload = JSON.parse(dataLines.join('\n'))
  } catch {
    return
  }

  switch (eventName) {
    case 'token':
      if (payload?.text) handlers.onToken(payload.text)
      break
    case 'tool':
      if (payload?.name) handlers.onTool(payload.name, payload.status)
      break
    case 'reset':
      handlers.onReset()
      break
    case 'summary':
      if (payload?.summary) handlers.onSummary(payload.summary)
      break
    case 'handover':
      handlers.onHandover()
      break
    case 'error':
      handlers.onError(payload?.message ?? 'Something went wrong.')
      break
    default:
      break
  }
}

export type SupportTicket = {
  id: string
  reference: string
  subject: string
  appKey?: string | null
  status: 'open' | 'ongoing' | 'done'
  createdAt: number
  updatedAt: number
  /** Latest reply from the Yodo1 team, shown under the ticket. */
  reply?: { text: string; at: number } | null
  replyCount?: number
}

/**
 * The signed-in developer's support tickets.
 *
 * The account is resolved server-side from the bearer token, so this cannot be
 * pointed at someone else's tickets by changing a parameter.
 */
export const fetchMyTickets = async (): Promise<SupportTicket[]> => {
  const token = localStorage.getItem(localStorageKey.ACCESS_TOKEN)
  if (!token || !env.aiUrl) return []
  try {
    const res = await fetch(`${env.aiUrl}/tickets`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) return []
    return (await res.json()).tickets ?? []
  } catch {
    return []
  }
}

export type SupportMessage = {
  id: string
  text: string
  at: number
  images?: { url: string; name?: string }[]
}

/**
 * Polls for replies written by a support person who has taken the chat over.
 *
 * Needed because a human reply arrives with no request from the customer -
 * without polling they would only see it after sending another message.
 */
export const fetchConversationUpdates = async (
  conversationId: string,
  since: number
): Promise<{ intercepted: boolean; messages: SupportMessage[] }> => {
  const token = localStorage.getItem(localStorageKey.ACCESS_TOKEN)
  if (!token || !env.aiUrl || !conversationId) {
    return { intercepted: false, messages: [] }
  }
  try {
    const res = await fetch(
      `${env.aiUrl}/conversation/updates?conversationId=${encodeURIComponent(
        conversationId
      )}&since=${since}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
    if (!res.ok) return { intercepted: false, messages: [] }
    return await res.json()
  } catch {
    return { intercepted: false, messages: [] }
  }
}
